import { Moon, Calendar, Clock, BookOpen, Sparkles, Bell, LogOut, LayoutDashboard, Dumbbell, Apple, Trophy, Users, PlaneTakeoff, ShieldAlert, MessageSquareHeart, Crown } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { Button } from "@/components/ui/button";
import PricingDialog from "@/components/PricingDialog";

const items = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard-link" },
  { key: "calendar", label: "Shifts", icon: Calendar, testid: "nav-calendar-link" },
  { key: "timeline", label: "Timeline", icon: Clock, testid: "nav-timeline-link" },
  { key: "log", label: "Sleep Log", icon: BookOpen, testid: "nav-log-link" },
  { key: "alertness", label: "Alertness", icon: ShieldAlert, testid: "nav-alertness-link" },
  { key: "jetlag", label: "Jet Lag", icon: PlaneTakeoff, testid: "nav-jetlag-link" },
  { key: "badges", label: "Badges", icon: Trophy, testid: "nav-badges-link" },
  { key: "team", label: "Team", icon: Users, testid: "nav-team-link" },
  { key: "reviews", label: "Reviews", icon: MessageSquareHeart, testid: "nav-reviews-link" },
  { key: "exercise", label: "Exercise", icon: Dumbbell, testid: "nav-exercise-link" },
  { key: "nutrition", label: "Nutrition", icon: Apple, testid: "nav-nutrition-link" },
  { key: "ai", label: "AI Advisor", icon: Sparkles, testid: "nav-ai-advisor-link" },
  { key: "alerts", label: "Alerts", icon: Bell, testid: "nav-alerts-link" },
];

export default function HeaderNav({ view, onView }) {
  const { user, logout } = useAuth();
  const { isPro, inTrial, daysLeft } = useSubscription();
  const [pricingOpen, setPricingOpen] = useState(false);

  return (
    <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#07090E]/70 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center glow-indigo">
            <Moon className="w-5 h-5 text-white" />
          </div>
          <span className="font-display text-lg font-bold tracking-tight hidden sm:block">CircaShift</span>
        </div>

        <nav className="flex items-center gap-1 overflow-x-auto no-scrollbar">
          {items.map((it) => {
            const active = view === it.key;
            return (
              <button
                key={it.key}
                data-testid={it.testid}
                onClick={() => onView(it.key)}
                className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition whitespace-nowrap
                  ${active ? "bg-indigo-500/15 text-indigo-200 border border-indigo-400/30" : "text-slate-400 hover:text-slate-100 hover:bg-white/5 border border-transparent"}`}
              >
                <it.icon className="w-4 h-4" />
                <span className="hidden md:inline">{it.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          {!isPro ? (
            <Button
              size="sm"
              onClick={() => setPricingOpen(true)}
              data-testid="btn-upgrade-pro"
              className="rounded-full bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-slate-900 font-semibold hidden sm:inline-flex"
            >
              <Crown className="w-3.5 h-3.5 mr-1" /> Upgrade
            </Button>
          ) : (
            <span
              className="pill bg-emerald-950/60 border-emerald-500/40 text-emerald-200 hidden sm:inline-flex"
              data-testid="pro-badge"
            >
              <Crown className="w-3 h-3" /> {inTrial ? `Trial · ${daysLeft}d` : "Pro"}
            </span>
          )}
          <div className="text-right hidden sm:block">
            <div className="text-xs uppercase tracking-widest font-mono-ui text-slate-500">{user?.persona}</div>
            <div className="text-sm text-slate-200 font-medium">{user?.name || user?.email}</div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            data-testid="btn-logout"
            onClick={logout}
            className="text-slate-400 hover:text-white hover:bg-white/5 rounded-full"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>
      <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} />
    </header>
  );
}
